import { useTheme } from '@mui/material'
import {
    Event4Program,
    HorseRacingExtentedInfo,
    LightHrEvent,
    TBetOperator
} from '@pturf-betin/betin-models-lib'
import {
    IBetContext,
    PMUSubmitParams,
    TBet,
    TBetRunner
} from 'contexts/BetContext'
import dayjs from 'dayjs'
import { BETS_CONFIG, GlobalThis, TBetType, TFormulaKey } from 'models'
import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import {
    getBetinOddsByRaceId,
    getBetinRaceById,
    getUpcomingHorseRacingProgramEvents
} from 'services/firestore'
import {
    betAffiliatesMapper,
    getBetDesignation,
    getQueryParam,
    hasSelectedAllBases,
    isDateValid,
    openAffiliateLink,
    parseBetDesignation,
    parseBetDesignationArray,
    sendTrackingEvent,
    uuid
} from 'utils'

const initialValue: IBetContext = {
    cUid: uuid(),
    race: null,
    error: null,

    betError: false,
    setBetError: () => undefined,

    selectedOperator: 'PMU',
    setSelectedOperator: () => undefined,

    selectedBetType: 'S',
    setSelectedBetType: () => undefined,

    selectedFormulaKey: 'UNIT',
    setSelectedFormulaKey: () => undefined,

    availableBetTypes: [],
    availableRunners: [],
    disabledRunners: [],

    bets: new Map(),
    setBets: () => undefined,

    selectedBets: new Map(),
    setFormulaKeyBets: () => undefined,
    setBetStake: () => undefined,
    setBetDesignation: () => undefined,
    setAllOrders: () => undefined,

    runnerSelectionVisible: false,
    setRunnerSelectionVisible: () => undefined,

    focusedInputId: null,
    setFocusedInputId: () => undefined,

    addHorseSelection: () => undefined,
    removeHorseSelection: () => undefined,

    handleTrackingEvent: () => new Promise<Response>(() => undefined),
    handleAffiliateLink: () => undefined,
    handlePmuSubmit: () => undefined,

    upcomingEvents: [],
    loadRaceData: async () => undefined,

    mainColor: '#00a591'
}

export const BetContext = createContext<IBetContext>(initialValue)
export const BetContextProvider = ({ children }: { children?: ReactNode }) => {
    const theme = useTheme()
    const [race, setRace] = useState<Event4Program | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [betError, setBetError] = useState<boolean>(false)
    const [selectedOperator, setSelectedOperator] = useState<TBetOperator>(
        (getQueryParam('operator') || 'PMU') as TBetOperator
    )
    const [selectedBetType, setSelectedBetType] = useState<TBetType>('S')
    const [selectedFormulaKey, setSelectedFormulaKey] =
        useState<TFormulaKey>('UNIT')
    const [availableBetTypes, setAvailableBetTypes] = useState<TBetType[]>([])
    const [availableRunners, setAvailableRunners] = useState<TBetRunner[]>([])
    const [disabledRunners, setDisabledRunners] = useState<TBetRunner[]>([])
    const [bets, setBets] = useState<IBetContext['bets']>(new Map())
    const [runnerSelectionVisible, setRunnerSelectionVisible] = useState(false)
    const [upcomingEvents, setUpcomingEvents] = useState<LightHrEvent[]>([])
    const [focusedInputId, setFocusedInputId] = useState<string | null>(null)
    const mainColor =
        theme.operators[selectedOperator]?.primary || initialValue.mainColor

    const raceId = getQueryParam('raceId')
    const raceDate = getQueryParam('raceDate')
    const mediaId = getQueryParam('mediaId')
    const navEnabled = getQueryParam('nav') === 'true'

    const selectedBets = useMemo(
        () =>
            (race
                ? bets
                      .get(race?.id || '')
                      ?.get(selectedBetType)
                      ?.get(selectedFormulaKey) || new Map()
                : new Map()) as Map<string, TBet>,
        [bets, race?.id, selectedBetType, selectedFormulaKey]
    )

    const setBetValue = (betId: string, value: TBet) => {
        if (!race) return
        const raceBets = bets.get(race.id) || new Map()
        const availableBetTypesFormulaKeys = new Map(
            raceBets.get(selectedBetType)
        )
        const currentFormulaKey = selectedBets

        currentFormulaKey.set(betId, value)
        raceBets.set(
            selectedBetType,
            availableBetTypesFormulaKeys.set(
                selectedFormulaKey,
                currentFormulaKey
            )
        )

        setBets(new Map(bets.set(race.id, raceBets)))
    }

    const setFormulaKeyBets = (
        formulaKey: TFormulaKey,
        newBets: Map<string, TBet>
    ) => {
        if (!race) return
        const raceBets = bets.get(race.id) || new Map()
        const availableBetTypesFormulaKeys = new Map(
            raceBets.get(selectedBetType)
        )

        availableBetTypesFormulaKeys.set(formulaKey, newBets)
        raceBets.set(selectedBetType, availableBetTypesFormulaKeys)
        setBets(new Map(bets.set(race.id, raceBets)))
    }

    const onError = (msg: string, opt?: unknown) => {
        setError(msg)
        console.error(msg, opt)
    }

    const loadUpcomingEvents = async (
        date: string
    ): Promise<LightHrEvent[]> => {
        const events = await getUpcomingHorseRacingProgramEvents(date)
        setUpcomingEvents(events)
        console.debug('[BETSLIP] loadUpcomingEvents()', { events })
        return events
    }

    const loadRaceData = async (id: string, date: string) => {
        const [raceData, runnersData] = await Promise.all([
            getBetinRaceById(id, date),
            getBetinOddsByRaceId(id)
        ])

        if (!raceData || !runnersData) {
            setRace(null)
            return onError(
                "Cette page n'existe pas ou est temporairement indisponible.",
                { id, date, raceData, runnersData }
            )
        }

        setError(null)
        setRace(raceData)

        const availableBetTypes: TBetType[] = []
        const rawBetTypes = raceData?.ops?.[selectedOperator]?.btys || []

        Object.keys(BETS_CONFIG).forEach((bType) => {
            if (
                rawBetTypes.find(
                    (rawBetType) =>
                        rawBetType.betType === bType && rawBetType.isActive
                )
            )
                availableBetTypes.push(bType as TBetType)
        })

        setSelectedBetType(
            availableBetTypes.includes('S') ? 'S' : availableBetTypes[0]
        )
        setAvailableBetTypes(availableBetTypes)

        if (!availableBetTypes.length)
            return onError(
                "Désolé, cette course n'est pas disponible pour cet opérateur.",
                { raceData }
            )

        const raceStatus =
            (raceData.exInf as HorseRacingExtentedInfo)?.raceInfo?.raceStatus ||
            'PRG'
        if (raceStatus !== 'PRG')
            return onError(
                "Désolé, cette course n'est plus ouverte aux paris.",
                { raceData }
            )

        const runnerOdds =
            runnersData?.odds.find((elem) => elem.operator === selectedOperator)
                ?.runners || []

        const runners: TBetRunner[] =
            (raceData?.exInf as HorseRacingExtentedInfo)?.runners?.map(
                (runner) => ({
                    ...runner,
                    liveOdd:
                        runnerOdds.find(
                            (runnerOdd) =>
                                runnerOdd.horseNumber === runner.saddle
                        )?.liveOdd || 0
                })
            ) || []

        console.debug('[BETSLIP] loadRaceData()', {
            raceData,
            runnersData,
            availableBetTypes,
            runners,
            bets
        })

        setAvailableRunners(runners.filter((runner) => runner.running))
        setDisabledRunners(runners.filter((runner) => !runner.running))
    }

    const addHorseSelection = ({
        horseNumber,
        odd,
        type = 'BASE'
    }: {
        horseNumber: string
        odd?: number
        type?: 'BASE' | 'ASSOCIATE'
    }) => {
        const newBets = selectedBets

        if (selectedBetType === 'S') {
            const bet: Omit<TBet, 'type' | 'id'> = {
                betType: selectedBetType,
                designation: horseNumber.toString(),
                operator: selectedOperator,
                raceId: +(race?.id || 0),
                formulaKey: 'UNIT',
                odd
            }

            const idG = uuid()
            newBets.set(idG, {
                ...bet,
                id: idG,
                formulaRisk: 'WIN'
            })

            const idP = uuid()
            newBets.set(idP, {
                ...bet,
                id: idP,
                formulaRisk: 'PLACE'
            })

            return setFormulaKeyBets(selectedFormulaKey, newBets)
        }

        const bets = Array.from(newBets.values())
        if (!bets.length) {
            const bet: Omit<TBet, 'type' | 'id'> = {
                betType: selectedBetType,
                designation: getBetDesignation({
                    base: type === 'BASE' ? [horseNumber] : [],
                    associates: type === 'ASSOCIATE' ? [horseNumber] : [],
                    formulaKey: selectedFormulaKey,
                    betType: selectedBetType
                }),
                operator: selectedOperator,
                raceId: +(race?.id || 0),
                formulaKey: selectedFormulaKey,
                stake: !['S', 'C'].includes(selectedBetType)
                    ? BETS_CONFIG[selectedBetType]?.minBet
                    : undefined
            }

            const idG = uuid()
            newBets.set(idG, {
                ...bet,
                id: idG,
                formulaRisk: 'WIN'
            })

            if (selectedBetType === 'C') {
                const idP = uuid()
                newBets.set(idP, {
                    ...bet,
                    id: idP,
                    formulaRisk: 'PLACE'
                })
            }
        } else {
            bets.forEach((bet) => {
                const { base, associates } = parseBetDesignation(
                    bet.designation
                )
                const rawBase = base.filter((hNum: string) => hNum !== 'X')
                let newBase =
                    type === 'BASE'
                        ? [...rawBase, horseNumber]
                        : base.includes(horseNumber) // Handles the case in which you select a horse for base + associates
                          ? base.filter((hNum) => hNum !== horseNumber)
                          : base

                // Disallows the user from selecting a new base if it already has the maximum
                if (
                    selectedFormulaKey !== 'UNIT' &&
                    type === 'BASE' &&
                    hasSelectedAllBases(bet.designation || '', selectedBetType)
                ) {
                    if (selectedBetType === 'C') newBase = [horseNumber]
                    else
                        return alert(
                            'Pour un champ total, vous ne pouvez pas sélectionner plus de chevaux de base.'
                        )
                }

                if (selectedFormulaKey === 'UNIT') {
                    return newBets.set(bet.id, {
                        ...bet,
                        designation: getBetDesignation({
                            base: [...base, horseNumber],
                            associates: [],
                            formulaKey: selectedFormulaKey,
                            betType: selectedBetType
                        })
                    })
                } else if (selectedFormulaKey === 'PART_WHEEL') {
                    const newAssociates =
                        type === 'ASSOCIATE'
                            ? [...associates, horseNumber]
                            : associates.includes(horseNumber) // Handles the case in which you select a horse for base + associates
                              ? associates.filter(
                                    (hNum) => hNum !== horseNumber
                                )
                              : associates

                    return newBets.set(bet.id, {
                        ...bet,
                        designation: getBetDesignation({
                            base: newBase,
                            associates: newAssociates,
                            formulaKey: selectedFormulaKey,
                            betType: selectedBetType
                        })
                    })
                } else if (selectedFormulaKey === 'WHEEL') {
                    return newBets.set(bet.id, {
                        ...bet,
                        designation: getBetDesignation({
                            base: newBase,
                            associates: [],
                            formulaKey: selectedFormulaKey,
                            betType: selectedBetType
                        })
                    })
                }
            })
        }

        setFormulaKeyBets(selectedFormulaKey, newBets)
    }

    const removeHorseSelection = (horseNumber: string) => {
        const newBets = selectedBets
        const bets = Array.from(newBets.values())

        if (selectedBetType === 'S') {
            newBets.forEach((bet) => {
                if (bet.designation === horseNumber.toString()) {
                    newBets.delete(bet.id)
                    return
                }

                newBets.set(bet.id, bet)
            })

            return setFormulaKeyBets(selectedFormulaKey, newBets)
        }

        bets.forEach((bet) => {
            const { base, associates } = parseBetDesignation(bet.designation)
            const rawBase = base.filter((hNum) => hNum !== 'X')
            const rawAssociates = associates.filter((hNum) => hNum !== 'X')

            // If designation contains horse number and only one horse is selected, remove entire bet
            if (
                base.includes(horseNumber) ||
                associates.includes(horseNumber)
            ) {
                if (rawBase.length + rawAssociates.length <= 1) {
                    newBets.delete(bet.id)
                    return
                }

                newBets.set(bet.id, {
                    ...bet,
                    designation: getBetDesignation({
                        base: base.filter((hNum) => hNum !== horseNumber),
                        associates: associates.filter(
                            (hNum) => hNum !== horseNumber
                        ),
                        formulaKey: selectedFormulaKey,
                        betType: selectedBetType
                    })
                })
            }
        })

        setFormulaKeyBets(selectedFormulaKey, newBets)
    }

    const setBetStake = (betId: string, stake?: number) => {
        const newBets = selectedBets

        newBets.set(betId, {
            ...(newBets.get(betId)! || {}),
            stake
        })

        setBetValue(betId, {
            ...(newBets.get(betId)! || {}),
            stake
        })
    }

    const setBetDesignation = (betId: string, designation: string) => {
        const newBets = selectedBets

        newBets.set(betId, {
            ...(newBets.get(betId)! || {}),
            designation
        })

        setBetValue(betId, {
            ...(newBets.get(betId)! || {}),
            designation
        })
    }

    const setAllOrders = (betId: string, allOrders: boolean) => {
        const newBets = selectedBets

        newBets.set(betId, {
            ...(newBets.get(betId)! || {}),
            allOrders
        })

        setBetValue(betId, {
            ...(newBets.get(betId)! || {}),
            allOrders
        })
    }

    const handleTrackingEvent: IBetContext['handleTrackingEvent'] = async ({
        eventType,
        actionType
    }) => {
        return sendTrackingEvent({
            cid: 'bs-rp-0',
            eventType,
            actionType,
            cUid: initialValue.cUid,
            operator: selectedOperator,
            betType: selectedBetType,
            formulaKey: selectedFormulaKey
        })
    }

    const handlePmuSubmit = ({
        designation,
        betAmount,
        optionalBetTypes = []
    }: PMUSubmitParams) => {
        if (!race) return

        const target = '38400'
        const raceName = `R${race.cNumber}C${race.nb}`
        const baseBetType =
            BETS_CONFIG[selectedBetType]?.operatorMatchingBetType?.[
                selectedOperator
            ] || selectedBetType

        const additionnalBetType = ['S', 'C'].includes(selectedBetType)
            ? optionalBetTypes
                  .map((wording) => wording.slice(0, 1).toUpperCase())
                  .join('')
            : ''
        // Si le nombre est a virgule (ex: 1.5), on le convertit en 1,50
        const formattedBetAmount = betAmount.toString().includes('.')
            ? Number(betAmount).toFixed(2).replace('.', ',')
            : betAmount

        const isDtlo =
            BETS_CONFIG[selectedBetType]?.order &&
            Array.from(selectedBets.values()).some((bet) => bet.allOrders)

        const body = `${raceName} ${baseBetType}${additionnalBetType} ${designation.replace('Champ total', '').replace('/', '/ ')} ${formattedBetAmount} euros${isDtlo ? ' DTLO' : ''}`

        window.open(`sms:${target};?body=${encodeURIComponent(body)}`, '_blank')
    }

    const handleAffiliateLink = (signUp?: boolean) => {
        if (!race) return

        return openAffiliateLink({
            cid: signUp ? 'bs-su-0' : 'bs-rp-0',
            cUid: initialValue.cUid,
            operator: selectedOperator,
            meetingNumber: race.cNumber!,
            raceNumber: race.nb!,
            raceDate: race.d!,
            tBet: betAffiliatesMapper(selectedBets)
        })
    }

    useEffect(() => {
        const onBetSlipClose = (e: MessageEvent) => {
            if (e.data !== 'onBetSlipClose') return
            setRunnerSelectionVisible(false)
        }

        window.addEventListener('message', onBetSlipClose)

        return () => {
            window.removeEventListener('message', onBetSlipClose)
        }
    }, [])

    useEffect(() => {
        const date =
            raceDate && raceDate !== 'undefined'
                ? raceDate
                : dayjs().format('YYYY-MM-DD')

        if (!isDateValid(date))
            return onError('Veuillez renseigner un paramètre raceDate valide.')
        if (!mediaId)
            return onError('Veuillez renseigner un paramètre mediaId valide.')

        if (raceId && raceId !== 'undefined') {
            void Promise.allSettled([
                navEnabled && loadUpcomingEvents(date),
                loadRaceData(raceId, date)
            ])
            return
        }

        loadUpcomingEvents(date).then((events) => {
            const nextEvents = events.filter(
                (event) =>
                    dayjs(event.stDt).toDate().getTime() > new Date().getTime()
            )
            if (!nextEvents.length)
                return onError(
                    "Désolé, aucune course n'est ouverte aux paris pour cette date."
                )

            void loadRaceData(nextEvents[0].id, date)
        })
    }, [selectedOperator])

    useEffect(() => {
        if (selectedBets.size > 0) return
        const runners = getQueryParam('runners')?.split(',')
        const baseCount = (BETS_CONFIG[selectedBetType]?.baseCount || 0) - 1

        if (runners && runners.length && availableRunners.length) {
            if (selectedFormulaKey === 'WHEEL') {
                const matchingRunner = availableRunners.find(
                    (runner) => runner.saddle === +runners[0]
                )
                if (!matchingRunner) return

                console.log('making selection')
                runners.slice(0, baseCount).forEach((hNum) => {
                    addHorseSelection({
                        horseNumber: hNum,
                        type: 'BASE',
                        odd: matchingRunner.liveOdd
                    })
                })
                return
            }

            runners.forEach((hNum, runnerIndex) => {
                const matchingRunner = availableRunners.find(
                    (runner) => runner.saddle === +hNum
                )
                if (!matchingRunner) return
                console.log(
                    'ADDING',
                    runnerIndex < baseCount || selectedFormulaKey === 'UNIT'
                        ? 'BASE'
                        : 'ASSOCIATE'
                )
                addHorseSelection({
                    horseNumber: hNum,
                    type:
                        runnerIndex <
                            (BETS_CONFIG[selectedBetType]?.baseCount || 0) -
                                1 || selectedFormulaKey === 'UNIT'
                            ? 'BASE'
                            : 'ASSOCIATE',
                    odd: matchingRunner.liveOdd
                })
            })
        }
    }, [selectedBetType, selectedFormulaKey, availableRunners])

    useEffect(() => {
        setSelectedFormulaKey('UNIT')
    }, [selectedBetType])

    useEffect(() => {
        const currentBets = Array.from(selectedBets.values() || [])
        const { base, associates } = parseBetDesignationArray(
            currentBets,
            selectedBetType
        )

        ;(globalThis as unknown as GlobalThis).getBetSlipContext = () => {
            void handleTrackingEvent({
                eventType: 'WINDOW',
                actionType: 'CLOSE'
            })
            return {
                raceId: race?.id,
                raceDate: race?.d,
                runners: [
                    ...base.filter((hNum) => hNum !== 'X'),
                    ...associates
                ].map((runner) => Number(runner)),
                operator: selectedOperator,
                bets: currentBets
            }
        }
    })

    const setDefaultRunners = () => {
        const searchParams = new URLSearchParams(window.location.search)
        const { base, associates } = parseBetDesignationArray(
            Array.from(selectedBets.values() || []),
            selectedBetType
        )

        const defaultRunners = [...base, ...associates].filter(
            (hNum) => hNum !== 'X'
        )

        if (defaultRunners.length > 0)
            searchParams.set('runners', defaultRunners.join(','))
        else searchParams.delete('runners')

        window.history.replaceState(
            {},
            '',
            `${window.location.pathname}?${searchParams.toString()}`
        )
    }

    const onBeforeFormulaKeyChange = (formulaKey: TFormulaKey) => {
        setDefaultRunners()
        setSelectedFormulaKey(formulaKey)
    }

    const onBeforeBetTypeChange = (betType: TBetType) => {
        setDefaultRunners()
        setSelectedBetType(betType)
    }

    return (
        <BetContext.Provider
            value={{
                ...initialValue,

                race,
                error,

                betError,
                setBetError,

                selectedOperator,
                setSelectedOperator,

                selectedBetType,
                setSelectedBetType: onBeforeBetTypeChange,

                selectedFormulaKey,
                setSelectedFormulaKey: onBeforeFormulaKeyChange,

                availableBetTypes,
                availableRunners,
                disabledRunners,

                bets,
                setBets,

                selectedBets,
                setFormulaKeyBets,
                setBetStake,
                setBetDesignation,
                setAllOrders,

                runnerSelectionVisible,
                setRunnerSelectionVisible,

                focusedInputId,
                setFocusedInputId,

                addHorseSelection,
                removeHorseSelection,

                handleTrackingEvent,
                handleAffiliateLink,
                handlePmuSubmit,

                upcomingEvents,
                loadRaceData,

                mainColor
            }}
        >
            {children}
        </BetContext.Provider>
    )
}

export const useBetContext = () => useContext(BetContext)
